import { Directive, DoCheck, ElementRef, HostBinding, Input, Output } from '@angular/core';
import { subjectFromObs } from 'core/rx-js';
import { BehaviorSubject, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, fromEvent, interval, map, merge, switchMap, take, takeUntil } from 'rxjs';
import { FixedScrollService } from './fixed-scroll.service';

@Directive({
  selector: '[fixedScroll]',
  standalone: true,
})
export class FixedScrollDirective implements DoCheck {

  readonly #destroy = new Subject<void>()
  readonly #checkSubject = new Subject<void>();

  readonly otherElementsSubject = new BehaviorSubject<HTMLElement[]>(null)
  @Input() get otherElements() {
    return this.otherElementsSubject.value
  }
  set otherElements(value) {
    this.otherElementsSubject.next(value)
  }

  get #element() {
    return this._elementRef.nativeElement;
  }

  readonly resizeObs = fromEvent(window, 'resize')

  readonly baseElementSubject = new BehaviorSubject<HTMLElement>(null)
  @Input() get baseElement() {
    return this.baseElementSubject.value
  }
  set baseElement(value) {
    this.baseElementSubject.next(value)
  }

  readonly disableSubject = new BehaviorSubject<boolean>(false)
  @Input() get disable() {
    return this.disableSubject.value
  }
  set disable(value) {
    this.disableSubject.next(value)
  }

  readonly additionalOffsetSubject = new BehaviorSubject<number>(0)
  @Input() get additionalOffset() {
    return this.additionalOffsetSubject.value
  }
  set additionalOffset(value) {
    this.additionalOffsetSubject.next(value)
  }

  readonly allOtherElementsObs = combineLatest([this.otherElementsSubject, this._fixedScrollService.otherElementsSubject]).pipe(
    map((arr) => arr.filter(Boolean).flatMap(item => item))
  )

  @Output('heightChange')
  readonly heightSubject = subjectFromObs({
    initialValue: null,
    until: this.#destroy,
    observable: combineLatest([this.allOtherElementsObs, this.baseElementSubject, this.additionalOffsetSubject]).pipe(
      takeUntil(this.#destroy),
      filter(([otherElements]) => !!otherElements),
      take(10),
      switchMap(([elements, baseElement, additionalOffset]) => {

        return merge(this.#checkSubject, this.resizeObs, interval(500)).pipe(
          debounceTime(100),
          map((v) => {
            const otherElementsHeight = elements.filter(Boolean).reduce((total, element) => total + element.offsetHeight, 0);
            if (baseElement) {
              return (baseElement.offsetHeight - otherElementsHeight - additionalOffset) + 'px';
            }

            return 'calc(100% - ' + (otherElementsHeight + additionalOffset) + 'px)'
          }),

        )
      }),
      distinctUntilChanged()
    )
  })


  get #initialHeight() {

    const otherElementsHeight = this.otherElements.filter(Boolean).reduce((total, element) => total + element.offsetHeight, 0);
    if (this.baseElement) {
      return (this.baseElement.offsetHeight - otherElementsHeight - this.additionalOffset) + 'px';
    }

    return 'calc(100% - ' + (otherElementsHeight + this.additionalOffset) + 'px)'
  }

  constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _fixedScrollService: FixedScrollService
  ) {


  }

  ngDoCheck(): void {
    this.#checkSubject.next()
  }


  ngAfterViewInit() {

    setTimeout(() => {
      this._elementRef.nativeElement.style.height = this.#initialHeight
    })

    combineLatest([this.heightSubject, this.disableSubject]).pipe(
      takeUntil(this.#destroy),
    )
      .subscribe(([height, disable]) => {
        if (disable) {

          this.#element.style.height = null;
          return;
        }

        this.#element.style.height = height;
      })

  }

  @HostBinding('style.overflow-y') overflowY = 'auto';


  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }
}
